/**
 * SSE CONTEXT - React Context wrapper for SSE Manager
 */

import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
} from "react";
import sseManager from "./SSEManager";

const SSEContext = createContext(null);

/**
 * SSE Provider Component
 */
export const SSEProvider = ({ children, baseUrl }) => {
  const [connectionStatuses, setConnectionStatuses] = useState({});
  const statusUpdateInterval = useRef(null);
  const companyId = localStorage.getItem("companyId");

  useEffect(() => {
    if (companyId) {
      console.log("[SSEProvider] Initializing with company:", companyId);

      // Update status less frequently
      statusUpdateInterval.current = setInterval(() => {
        const statuses = sseManager.getAllStatuses();
        setConnectionStatuses(statuses);
      }, 10000); // Increased to 10 seconds
    }

    return () => {
      console.log("[SSEProvider] Cleaning up");
      if (statusUpdateInterval.current) {
        clearInterval(statusUpdateInterval.current);
      }
    };
  }, [companyId]);

  const contextValue = {
    sseManager,
    companyId,
    connectionStatuses,
  };

  return (
    <SSEContext.Provider value={contextValue}>{children}</SSEContext.Provider>
  );
};

export const useSSE = () => {
  const context = useContext(SSEContext);

  if (!context) {
    throw new Error("useSSE must be used within SSEProvider");
  }

  return context;
};


export const useAgentSSE = (agentType, onMessage, options = {}) => {
  const { sseManager, companyId, connectionStatuses } = useSSE();
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState(null);
  const unsubscribeRef = useRef(null);
  
  const onMessageRef = useRef(onMessage);
  
  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);

  useEffect(() => {
    if (agentType === null) {
      console.log("[useAgentSSE] SSE disabled (agentType is null)");
      setIsConnected(false);
      setError(null);
      return;
    }

    if (!companyId) {
      console.warn("[useAgentSSE] Missing companyId");
      setIsConnected(false);
      return;
    }

    const channelId = `${agentType}_${companyId}`;

    console.log(`[useAgentSSE] Subscribing to channel: ${channelId} for agent: ${agentType}`);

    const handleMessage = (data, channel) => {
      // Handle connection status updates
      if (data.type === "_connection_status") {
        console.log(`[useAgentSSE] Connection status:`, data.status);
        setIsConnected(data.status === "connected");
        
        if (data.status === "error" || data.status === "failed") {
          setError(data.error || data.reason || "Connection failed");
        } else if (data.status === "connected") {
          setError(null);
        }
        return;
      }

      // ⚠️ CRITICAL FIX: Parse nested JSON string in message field
      let parsedData = data;
      
      if (data.message && typeof data.message === 'string') {
        try {
          console.log('[useAgentSSE] Parsing nested message string:', data.message);
          parsedData = JSON.parse(data.message);
          console.log('[useAgentSSE] Parsed data:', parsedData);
        } catch (parseError) {
          console.error('[useAgentSSE] Failed to parse message:', parseError);
          // If parsing fails, use original data
          parsedData = data;
        }
      }

      // Filter messages by agent type if needed
      if (parsedData.agent && parsedData.agent !== agentType) {
        console.log(`[useAgentSSE] Ignoring message for different agent: ${parsedData.agent}`);
        return;
      }

      // Pass to user's callback
      try {
        onMessageRef.current(parsedData, channel);
      } catch (err) {
        console.error(`[useAgentSSE] Error in message handler:`, err);
        setError(err.message);
      }
    };

    const unsubscribe = sseManager.subscribe(channelId, handleMessage, options);
    unsubscribeRef.current = unsubscribe;

    setTimeout(() => {
      const status = sseManager.getStatus(channelId);
      if (status) {
        setIsConnected(status.connected);
      }
    }, 100);

    return () => {
      console.log(`[useAgentSSE] Unsubscribing from ${channelId}`);
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [agentType, companyId, sseManager]);

  return {
    isConnected,
    error,
    channelId: companyId || null,
    status: connectionStatuses[companyId] || null,
  };
};

export default SSEContext;